import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { getPost, formatDate } from '../../assets/info/Posts';
import RichText from '../RichText';
import styles from './PostPage.module.scss';

function Block({ block }) {
  switch (block.type) {
    case 'h2':
      return (
        <h2 id={block.id} className={styles.h2}>
          {block.text}
        </h2>
      );
    case 'h3':
      return (
        <h3 id={block.id} className={styles.h3}>
          {block.text}
        </h3>
      );
    case 'list': {
      const List = block.ordered ? 'ol' : 'ul';
      return (
        <List className={styles.list}>
          {block.items.map((item, index) => (
            <li key={index}>
              <RichText text={item} />
            </li>
          ))}
        </List>
      );
    }
    case 'quote':
      return (
        <blockquote className={styles.quote}>
          <RichText text={block.text} />
          {block.cite && <cite className={styles.cite}>— {block.cite}</cite>}
        </blockquote>
      );
    case 'code':
      return (
        <pre className={styles.code}>
          <code>{block.code}</code>
        </pre>
      );
    case 'image':
      return (
        <figure className={styles.figure}>
          <img src={block.src} alt={block.alt || ''} />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    case 'embed':
      return (
        <figure className={styles.embed}>
          <div className={styles.frame} style={block.ratio ? { aspectRatio: block.ratio } : undefined}>
            <iframe src={block.src} title={block.title || 'Embedded demo'} loading={'lazy'} />
          </div>
          {block.caption && (
            <figcaption>
              <RichText text={block.caption} />
            </figcaption>
          )}
        </figure>
      );
    case 'divider':
      return <hr className={styles.divider} />;
    case 'p':
    default:
      return (
        <p className={styles.paragraph}>
          <RichText text={block.text} />
        </p>
      );
  }
}

// A single post from one collection. `basePath` is where the back link goes,
// `backLabel` is what it says.
export default function PostPage({ posts, basePath, backLabel }) {
  const { slug } = useParams();
  const post = getPost(posts, slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!post) {
    return (
      <Box className={styles.post}>
        <h1 className={styles.title}>Post not found</h1>
        <p className={styles.paragraph}>That post doesn’t exist — it may have been renamed.</p>
        <Link to={basePath} className={styles.back}>
          ← {backLabel}
        </Link>
      </Box>
    );
  }

  return (
    <Box component={'article'} className={styles.post}>
      <Link to={basePath} className={styles.back}>
        ← {backLabel}
      </Link>
      <h1 className={styles.title}>
        <RichText text={post.title} />
      </h1>
      <Box className={styles.meta}>
        <span>{formatDate(post.date)}</span>
        {post.readTime && <span>{post.readTime} read</span>}
        {post.tags && post.tags.map((tag) => <span key={tag}>{tag}</span>)}
      </Box>
      <Box className={styles.body}>
        {post.content.map((block, index) => (
          <Block key={index} block={block} />
        ))}
      </Box>
    </Box>
  );
}
